import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { Mail } from 'lucide-react'
import UserIcon from 'components/users/UserIcon'
import { UserStream } from 'components/filters/FilterIcon'
import { withDepth } from 'components/filters/useStreamFilters'
import useGetUser from 'refactor/hooks/api/useGetUser'
import useOpenRight from 'refactor/hooks/useOpenRight'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { getCachedUser } from 'state/cache'
import { ANSWER_HEIGHT } from 'state/PanelState'
import { directMessageCounterpartId } from 'lib/directMessages'
import { markPanelWithDirectMessageLetter } from 'lib/directMessageLetterPanel'
import type { Quest } from 'types/quests'
import styles from './direct-message-head.module.scss'

type Counterpart = {
    id?: string | number
    name?: string
    username?: string
    bio?: string
    description?: string
    banner_url?: string | null
    followers_count?: number
    is_following_current_user?: boolean
    is_followed_by_current_user?: boolean
}

interface DirectMessageHeadProps {
    answerId: string
    quest: Quest
    // 'hero' in the open conversation; 'compact' on a stream card (Messages).
    variant?: 'hero' | 'compact'
    onClickTitle?: (ev: React.MouseEvent) => void
}

function relationshipLabel(counterpart: Counterpart | null): string | null {
    const followsYou = counterpart?.is_following_current_user === true
    const youFollow = counterpart?.is_followed_by_current_user === true
    if (followsYou && youFollow) return 'You follow each other'
    if (followsYou) return 'Follows you'
    if (youFollow) return 'You follow them'
    return null
}

// The head of a direct-message conversation. The server's head is a system
// post naming both people; the viewer only needs who they are talking to. The
// open conversation leads with a sealed letter: the other person's banner
// folded into an envelope flap, sealed by their avatar, so a direct message is
// recognizable at a glance and every conversation carries its person's look.
const DirectMessageHead: React.FC<DirectMessageHeadProps> = ({
    answerId,
    quest,
    variant = 'hero',
    onClickTitle,
}) => {
    const { panel } = usePanelContext()
    const { openRight } = useOpenRight(panel?.panelId)
    const counterpartId = directMessageCounterpartId(quest, gon.currentUser?.id)
    const { user: fetchedCounterpart } = useGetUser(counterpartId || undefined, {
        enabled: !!counterpartId,
        refetchOnMount: false,
    })
    const counterpart = (fetchedCounterpart || getCachedUser(counterpartId)) as Counterpart | null
    const name = counterpart?.username || counterpart?.name || ''

    // banner_url is versioned by blob id (as on the profile), so a changed
    // banner is a changed URL.
    const bannerUrl = counterpart?.banner_url
        ? `${window.knovApiUrl || ''}${counterpart.banner_url}`
        : null
    const [bannerFailed, setBannerFailed] = useState(false)
    useEffect(() => {
        setBannerFailed(false)
    }, [bannerUrl])

    // The open letter marks its panel (lib/directMessageLetterPanel), before paint
    // so the panel never shows its header row above the letter.
    const letterRef = useRef<HTMLDivElement | null>(null)
    useLayoutEffect(() => {
        if (variant !== 'hero') return
        return markPanelWithDirectMessageLetter(letterRef.current)
    }, [variant])

    const banner =
        bannerUrl && !bannerFailed ? (
            <img
                className={styles.bannerImage}
                src={bannerUrl}
                alt=""
                onError={() => setBannerFailed(true)}
            />
        ) : (
            <div className={styles.bannerDefault} />
        )

    if (variant === 'compact') {
        // The Messages card: the open conversation's sealed letter, folded
        // small. A strip of their banner as the flap, their avatar as the seal,
        // then who they are, in the head post's usual height.
        const line = [
            String(counterpart?.bio || counterpart?.description || '').trim(),
            relationshipLabel(counterpart),
        ]
            .filter(Boolean)
            .join(' · ')
        return (
            <div
                className={cn(styles.letterStrip, onClickTitle && styles.clickable)}
                data-answer-id={answerId}
                // It renders in the head post's place, so it is that post's row
                // (lib/panelEndGutter PANEL_SCROLL_ROW_SELECTOR).
                data-testid="answer-root"
                style={{ minHeight: ANSWER_HEIGHT }}
                onClick={onClickTitle}
            >
                <div className={cn(styles.flap, styles.stripFlap)}>
                    <div className={styles.flapEdge} />
                    <div className={cn(styles.flapFace, styles.stripFace)}>{banner}</div>
                </div>
                {counterpartId && (
                    <div className={cn(styles.seal, styles.stripSeal)}>
                        <UserIcon
                            user={counterpart || { id: counterpartId }}
                            size="medium"
                            contextStyles={styles.stripAvatar}
                        />
                        <span className={cn(styles.sealBadge, styles.stripBadge)}>
                            <Mail size={10} strokeWidth={2.6} />
                        </span>
                    </div>
                )}
                <span className={styles.stripName}>{name}</span>
                {line && <span className={styles.stripLine}>{line}</span>}
            </div>
        )
    }

    const openProfile = (ev: React.MouseEvent) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (counterpartId) openRight(withDepth({ user: counterpartId }))
    }

    const bio = String(counterpart?.bio || counterpart?.description || '').trim()
    const followers = Number(counterpart?.followers_count) || 0
    const meta = [
        relationshipLabel(counterpart),
        followers > 0 ? `${followers} ${followers === 1 ? 'follower' : 'followers'}` : null,
    ].filter(Boolean)

    return (
        // The head post's row. A conversation with no messages yet has no other
        // row, and the panel sizes the card's run down to the composer from it.
        // Opening the conversation from its card in Messages targets this post,
        // and the panel stays hidden until it finds the row (lib/threadOpenReveal).
        // data-direct-message-letter: the panel opens on this letter alone, with
        // no header row above it (the panel mark, lib/directMessageLetterPanel),
        // and the place bar waits until it has scrolled away (PanelPlaceBar
        // useLetterCovered).
        <div
            ref={letterRef}
            className={styles.hero}
            data-answer-id={answerId}
            data-testid="answer-root"
            data-direct-message-letter="true"
        >
            <div className={styles.flap}>
                <div className={styles.flapEdge} />
                <div className={styles.flapFace}>{banner}</div>
            </div>
            {counterpartId && (
                <div className={styles.seal}>
                    <UserStream
                        user={counterpart || { id: counterpartId }}
                        size="large"
                        contextStyles={styles.sealAvatar}
                    />
                </div>
            )}
            <div className={styles.heroBody}>
                <div className={styles.eyebrow}>
                    <Mail size={13} strokeWidth={2.5} aria-hidden />
                    Direct message
                </div>
                <button type="button" className={styles.heroName} onClick={openProfile}>
                    {name}
                </button>
                {bio && <div className={styles.heroBio}>{bio}</div>}
                {meta.length > 0 && <div className={styles.heroMeta}>{meta.join(' · ')}</div>}
            </div>
        </div>
    )
}

export default DirectMessageHead
